import * as z from 'zod/v4';

/** A wrapper for fetch that expects a JSON and throws a typed error if fails */
async function fetchJson({ url, options, }) {
    const { method, headers = {}, body, credentials = "include" } = options;
    const finalHeaders = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "Content-Type": "application/json",
        ...headers,
    };
    const response = await fetch(url, {
        method,
        headers: finalHeaders,
        body: body ? JSON.stringify(body) : undefined,
        credentials,
    });
    if (!response.ok) {
        const contentType = response.headers.get("content-type") || "";
        let errorBody;
        try {
            errorBody = contentType.includes("application/json")
                ? await response.json()
                : await response.text();
        }
        catch {
            errorBody = null;
        }
        const error = {
            name: "FetchingError",
            message: `Request failed with status ${response.status}`,
            status: response.status,
            errorBody,
        };
        throw error;
    }
    return response.json();
}

/**
 * Reusable abstraction wrapping the logic to communicating with core api
 * Allows to make type-safe GET and POST requests
 */
class BaseApiClient {
    baseUrl;
    headers;
    onLogAssertion;
    urls = {
        usersV3: "/v3/users",
        dashboardV3: "/v3/dashboard",
    };
    constructor({ baseUrl, headers, onLogAssertion }) {
        this.baseUrl = baseUrl;
        this.headers = headers ?? {};
        this.onLogAssertion = onLogAssertion;
    }
    /** Sends a GET request and return the data property */
    async get(path) {
        try {
            const response = await fetchJson({
                url: `${this.baseUrl}${path}`,
                options: {
                    method: "GET",
                    headers: this.headers,
                },
            });
            return response.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /** Sends a POST request and return the data property */
    async post(path, body) {
        try {
            const response = await fetchJson({
                url: `${this.baseUrl}${path}`,
                options: {
                    method: "POST",
                    headers: this.headers,
                    body,
                },
            });
            return response.data;
        }
        catch (error) {
            this.handleError(error);
        }
    }
    /**
     * Check the error type
     * Throws an error with HTTP status and body if it is in type of fetchError
     * otherwise a generic error
     */
    handleError(error) {
        if (this.isFetchError(error)) {
            throw new Error(`Fetch failed: ${error.status} ${error.errorBody}`);
        }
        else {
            throw new Error(`Unknown error: ${error}`);
        }
    }
    isFetchError(error) {
        return (typeof error === "object" &&
            error !== null &&
            "name" in error &&
            error.name === "FetchError" &&
            "status" in error &&
            "errorBody" in error);
    }
    /**
     * Validates an object against a Zod schema.
     * @param obj The object to validate against the schema.
     * @param schema The Zod schema to validate against.
     * @param routeName Optional route name for logging purposes.
     * @returns The same object, no modifications are made.
     */
    assertSchema(obj, schema, routeName) {
        const result = schema.safeParse(obj);
        if (!result.success && this.onLogAssertion) {
            const errorMessage = `Assertion failed for route "${routeName}": ${result.error.issues
                .map((e) => e.message)
                .join(", ")}`;
            this.onLogAssertion(errorMessage, result.error);
        }
        return result.success ? result.data : obj;
    }
}

const WorkspaceRoleSchema = z.enum([
    "Administrator",
    "owner",
    "Enterprise Admin",
    "Enterprise Viewer",
    "Company Admin",
    "Group Manager",
    "Project Manager",
    "Company Member",
]);
z.enum([
    /** Equal to enterprise admins. Have the highest role in company */
    "COMPANY_EXECUTIVE",
    /** Equal to Enterprise viewer */
    "COMPANY_VIEWER",
    /** Equal to group manager */
    "COMPANY_MANAGER",
    /**
     * A group member role with ability to view the group's details, setting, members and all projects,
     * including their contents and all projects of the group (including contents).
     */
    "GROUP_VIEWER",
    /** Project manager. In project view (EUserProjectRole) it is equal to project owner */
    "PROJECT_MANAGER",
    /**
     * Every role in company is also a member of it. This is the default role for every user in a company
     * If a new member is invited as a project viewer of a project, they become member of company
     * If that member is removed from project (as viewer), they still remain as member of company
     */
    "MEMBER",
]);

/** All available workspace types */
const WorkspaceTypeSchema = z.enum([
    /** A workspace connected to a dashboard */
    "company",
    /** Workspaces only accessible to HoloBuilder admins. Providing access to admin services (e.g. the admin panel) */
    "admin",
]);
/** A workspace containing the api, icon etc. */
const WorkspaceSchema = z.object({
    /** The apiUrl of the workspace, i.e. where to send (login) requests */
    apiUrl: z.string(),
    /** This is for companies that have a Holobuilder subdomain and otherwise will be undefined */
    domain: z.string().optional(),
    /** Icon to show with the workspace, e.g. Company Logo for Dashboards. Can be an empty string if not available */
    icon: z.string().optional(),
    /** Name of the workspace */
    name: z.string(),
    /** The highest role the user has in the workspace */
    role: WorkspaceRoleSchema,
    /** The type of the workspace, can be either a normal (company workspace) or the admin panel */
    type: WorkspaceTypeSchema,
    /** Url to workspace, e.g. dashboard.xxx/workspace-id */
    url: z.string(),
});

/** Defines the schema for the GetCurrentUsersWorkspaces response */
const GetCurrentUsersWorkspacesSchema = z.object({
    /** List of workspaces */
    workspaces: z.array(WorkspaceSchema),
});

/** All the APIs for fetching and updating workspaces */
class UsersClient extends BaseApiClient {
    /**
     * Gets an object representing all workspaces that the current user belongs to.
     * Can return undefined if the user is not logged in.
     */
    async getCurrentUsersWorkspaces() {
        const response = await this.get(`${this.urls.usersV3}/me/workspaces`);
        return this.assertSchema(response, GetCurrentUsersWorkspacesSchema, "getCurrentUsersWorkspaces");
    }
}

/** Client for interacting with the Core API. */
class CoreApiClient {
    users;
    constructor({ baseUrl, headers, onLogAssertion, }) {
        this.users = new UsersClient({ baseUrl, headers, onLogAssertion });
    }
}

var index = /*#__PURE__*/Object.freeze({
    __proto__: null
});

export { CoreApiClient, index as CoreApiTypes };
